<template>
  <BootstrapAlert v-if="fatalError" variant="danger">{{ fatalError }}</BootstrapAlert>
  <PenaltyFormPresentational
    v-model:description="description"
    v-model:name="name"
    v-model:percentage="percentage"
    v-model:service-credit="serviceCredit"
    v-model:slo="slo"
    :slo-options="sloOptions"
    :error-messages="errorMessages"
    :loading="loading"
    :server-error="serverError"
    :disabled="!!fatalError"
    @submit="submit"
  />
</template>
<script lang="ts" setup>
import {Detail} from '@/api/errorMessage';
import {ServiceLevelObjectivePenaltyCreateUpdate} from '@/stores/userManagement/userManagement';

const props = defineProps<{
  isUpdateForm: boolean;
  id?: number;
}>();

const router = useRouter();
const route = useRoute();

const sloId = computed(() => Number(route.query.sloId)) as ComputedRef<number>;

// Stores
const serviceLevelObjectivesStore = useServiceLevelObjectivesStore();
const getServiceLevelObjectives = computed(
  () => serviceLevelObjectivesStore.getServiceLevelObjectives,
);

// Used to store errors that are never supposed to happen
const fatalError = ref<string | undefined>();
const fatal = (error: string): Error => {
  fatalError.value = error;
  return Error(`Fatal error: ${error}`);
};

const loading = ref(false);

// Form data
const description = ref('');
const name = ref('');
const percentage = ref(0);
const serviceCredit = ref(0);
const slo = ref('');
const sloOptions = computed(() => getServiceLevelObjectives.value.map(({name}) => name));
const errorMessages = ref(new Map());
const serverError = ref(false);

const submit = async (form: globalThis.Ref<HTMLFormElement | null>) => {
  try {
    loading.value = true;
    const sloId = getServiceLevelObjectives.value.find(({name}) => name === slo.value)?.id;
    if (!sloId) throw fatal('Missing service level objective');
    const penalty: ServiceLevelObjectivePenaltyCreateUpdate = {
      slo_id: sloId,
      name: name.value,
      description: description.value,
      percentage: percentage.value,
      service_credit: serviceCredit.value,
    };
    if (!props.isUpdateForm) {
      await serviceLevelObjectivesStore.createPenalty(penalty);
      router.go(-1);
    } else {
      const updatePenalty: ServiceLevelObjectivePenaltyCreateUpdate = {
        ...penalty,
        id: Number(props.id),
      };
      await serviceLevelObjectivesStore.updatePenalty(updatePenalty);
      router.go(-1);
    }
  } catch (errorMessage: any) {
    serverError.value = true;
    errorMessages.value = new Map();
    form.value?.classList.remove('was-validated');
    if (typeof errorMessage !== 'string' && errorMessage.length > 0) {
      errorMessage.forEach((d: Detail) => errorMessages.value.set(d.loc[1], d.msg));
    }
    form.value?.classList.remove('was-validated');
    console.log(errorMessage);
  } finally {
    loading.value = false;
  }
};

const load = async () => {
  // Try find the SLO in store state using the sloId route param
  let foundSlo = getServiceLevelObjectives.value.find((s) => s.id === sloId.value);
  // If the item is missing, try to fetch it
  if (foundSlo === undefined) {
    try {
      const data = await serviceLevelObjectivesStore.readServiceLevelObjectiveById(sloId.value);
      foundSlo = data;
    } catch (error) {
      console.log(error);
    }
  }
  slo.value = foundSlo?.name || '';
  // Try find the penalty using the foundSlo
  if (props.isUpdateForm === true && foundSlo !== undefined) {
    // Try find the item in store state
    let penalty = foundSlo.penalties.find((p) => p.id === props.id);
    // If the item is missing, try to fetch it
    if (penalty === undefined && props.id !== undefined) {
      try {
        const data = await serviceLevelObjectivesStore.readPenaltyById(props.id);
        penalty = data;
      } catch (error) {
        console.log(error);
      }
    }
    if (penalty !== undefined) {
      // Set the form data
      description.value = penalty.description;
      name.value = penalty.name;
      percentage.value = penalty.percentage;
      serviceCredit.value = penalty.service_credit;
    }
  }
};

onMounted(() => load());
</script>
